import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';
import SeverityChip from './SeverityChip';

interface AlertConfig {
  enabled: boolean;
  recipients: string[];
  alert_on_failed_run: boolean;
  alert_on_consecutive: number;
  alert_on_audit_critical: boolean;
  cooldown_minutes: number;
  updated_at?: string;
}

const alertStatusSeverity = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s === 'sent' || s === 'ok' || s === 'success') return 'OK';
  if (s === 'failed' || s === 'error') return 'CRITICAL';
  if (s === 'suppressed' || s === 'skipped') return 'MINOR';
  return 'MAJOR';
};

const AlertsTab: React.FC = () => {
  const qc = useQueryClient();

  const { data: cfg } = useQuery<AlertConfig>({
    queryKey: ['admin-alert-config'],
    queryFn: adminAPI.getAlertConfig,
    staleTime: 30000,
  });

  const { data: logData } = useQuery({
    queryKey: ['admin-alert-log'],
    queryFn: () => adminAPI.getAlertLog(100),
    staleTime: 15000,
    refetchInterval: 30000,
  });

  // Local form state, initialized from server config
  const [enabled, setEnabled] = useState(false);
  const [recipientsStr, setRecipientsStr] = useState('');
  const [alertOnFailed, setAlertOnFailed] = useState(true);
  const [alertOnAuditCritical, setAlertOnAuditCritical] = useState(true);
  const [alertOnConsecutive, setAlertOnConsecutive] = useState<number>(3);
  const [cooldownMinutes, setCooldownMinutes] = useState<number>(60);
  const [saveResult, setSaveResult] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (!cfg) return;
    setEnabled(!!cfg.enabled);
    setRecipientsStr((cfg.recipients || []).join(', '));
    setAlertOnFailed(!!cfg.alert_on_failed_run);
    setAlertOnAuditCritical(!!cfg.alert_on_audit_critical);
    setAlertOnConsecutive(cfg.alert_on_consecutive ?? 3);
    setCooldownMinutes(cfg.cooldown_minutes ?? 60);
  }, [cfg]);

  const saveMut = useMutation({
    mutationFn: (updates: Partial<AlertConfig>) => adminAPI.updateAlertConfig(updates),
    onSuccess: (resp) => {
      setSaveResult('Saved.');
      qc.setQueryData(['admin-alert-config'], resp);
      qc.invalidateQueries({ queryKey: ['admin-actions'] });
    },
    onError: (err: any) => {
      setSaveResult(`Error: ${err?.response?.data?.detail || err?.message || 'unknown'}`);
    },
  });

  const testMut = useMutation({
    mutationFn: () => adminAPI.testAlert(),
    onSuccess: (resp) => {
      setTestResult(`Test sent — status: ${resp.status} · recipients: ${(resp.recipients || []).join(', ') || '—'}`);
      qc.invalidateQueries({ queryKey: ['admin-alert-log'] });
      qc.invalidateQueries({ queryKey: ['admin-actions'] });
    },
    onError: (err: any) => {
      setTestResult(`Error: ${err?.response?.data?.detail || err?.message || 'unknown'}`);
    },
  });

  const handleSave = () => {
    const recipients = recipientsStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    saveMut.mutate({
      enabled,
      recipients,
      alert_on_failed_run: alertOnFailed,
      alert_on_audit_critical: alertOnAuditCritical,
      alert_on_consecutive: alertOnConsecutive,
      cooldown_minutes: cooldownMinutes,
    });
  };

  const alerts = logData?.alerts || [];

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Panel label="ALERT CONFIG">
        {!cfg ? (
          <Typography color="textSecondary">Loading config...</Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">ENABLED</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={alertOnFailed}
                    onChange={(e) => setAlertOnFailed(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">ALERT ON FAILED RUN</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={alertOnAuditCritical}
                    onChange={(e) => setAlertOnAuditCritical(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">ALERT ON AUDIT CRITICAL</Typography>}
              />
            </Box>

            <TextField
              label="Recipients (comma-separated emails)"
              value={recipientsStr}
              onChange={(e) => setRecipientsStr(e.target.value)}
              fullWidth
              size="small"
              helperText="Example: ops@example.com, alerts@example.com"
            />

            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                label="Alert on consecutive failures"
                type="number"
                size="small"
                value={alertOnConsecutive}
                onChange={(e) => {
                  const n = Math.max(1, Math.min(100, parseInt(e.target.value || '1', 10)));
                  setAlertOnConsecutive(n);
                }}
                inputProps={{ min: 1, max: 100 }}
                sx={{ width: 240 }}
              />
              <TextField
                label="Cooldown (minutes)"
                type="number"
                size="small"
                value={cooldownMinutes}
                onChange={(e) => {
                  const n = Math.max(0, Math.min(10080, parseInt(e.target.value || '0', 10)));
                  setCooldownMinutes(n);
                }}
                inputProps={{ min: 0, max: 10080 }}
                sx={{ width: 240 }}
              />
            </Box>

            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                disabled={saveMut.isPending}
                onClick={handleSave}
              >
                {saveMut.isPending ? '[ SAVING... ]' : '[ SAVE ]'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                disabled={testMut.isPending}
                onClick={() => testMut.mutate()}
              >
                {testMut.isPending ? '[ SENDING... ]' : '[ SEND TEST EMAIL ]'}
              </Button>
              {cfg.updated_at && (
                <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                  updated {new Date(cfg.updated_at).toLocaleString()}
                </Typography>
              )}
            </Box>

            {saveResult && (
              <Typography
                variant="caption"
                sx={{ color: saveResult.startsWith('Error') ? '#ff3344' : '#00ff41' }}
              >
                {saveResult}
              </Typography>
            )}
            {testResult && (
              <Typography
                variant="caption"
                sx={{ color: testResult.startsWith('Error') ? '#ff3344' : '#00ff41' }}
              >
                {testResult}
              </Typography>
            )}
          </Box>
        )}
      </Panel>

      <Panel label="ALERT HISTORY">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SENT</TableCell>
                <TableCell>TYPE</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell>SUBJECT</TableCell>
                <TableCell>RECIPIENTS</TableCell>
                <TableCell>ERROR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="caption" color="textSecondary">
                      No alerts logged yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                alerts.map((a: any) => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {a.sent_at ? new Date(a.sent_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="'VT323', monospace">
                        {a.alert_type}
                      </Typography>
                    </TableCell>
                    <TableCell><SeverityChip severity={alertStatusSeverity(a.status)} /></TableCell>
                    <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.subject || '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {Array.isArray(a.recipients) ? a.recipients.join(', ') : (a.recipients || '—')}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: a.error ? '#ff3344' : undefined }}>
                      {a.error || ''}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Panel>
    </Box>
  );
};

export default AlertsTab;
